import React from "react";
import {
    Box,
    Typography,
    Button,
    Container,
    Grid,
    Card,
    CardContent,
    useTheme,
    Stack
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { tokens } from "../../theme";
import { hoverLift, imageZoom, iconChip, sectionPadding, SECTION_Y } from "../../styles/surfaces";
import Reveal from "../../components/Reveal";
import videoSrc from '../../images/video.mp4';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EngineeringIcon from '@mui/icons-material/Engineering';
import StarIcon from '@mui/icons-material/Star';
import SecurityIcon from '@mui/icons-material/Security';
import { Link } from "react-router-dom";

const Home = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const features = [
        {
            icon: <EngineeringIcon sx={{ fontSize: 28 }} />,
            title: "Expert Craftsmanship",
            description: "Our skilled team brings years of experience and dedication to every project."
        },
        {
            icon: <StarIcon sx={{ fontSize: 28 }} />,
            title: "Quality Materials",
            description: "We use only the highest quality materials to ensure lasting durability."
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 28 }} />,
            title: "Guaranteed Work",
            description: "Your satisfaction is our priority, backed by our quality guarantee."
        }
    ];

    const services = [
        {
            title: "Curtain Wall",
            image: "https://images.unsplash.com/photo-1632759145351-1d592919f522?auto=format&fit=crop&w=800",
            description: "Stick and unitised curtain walling for new-build and refurbishment projects, delivered with precision installation and quality assurance.",
            features: ["Stick & Unitised Systems", "New-Build & Refurbishment", "Precision Installation"]
        },
        {
            title: "Cladding",
            image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800",
            description: "Façade remediation and replacement works, including cladding, glazing, and façade component replacement.",
            features: ["Cladding & Glazing Replacement", "Component Replacement", "Building Safety Compliance"]
        },
        {
            title: "Remediation",
            image: "https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?auto=format&fit=crop&w=800",
            description: "Façade remediation and replacement works, with EPDM, insulation, and breather membrane installation.",
            features: ["EPDM & Insulation Installation", "Breather Membrane Installation", "Building Safety Compliance"]
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            {/* `isolation: isolate` makes this a stacking context, so the three z-indexes
                below are resolved against each other and cannot escape into the document.
                Without it the video's z-index would be measured against the page root,
                where anything painting a background over the canvas hides it. Keep the
                stack ordered 0/1/2 bottom-to-top rather than leaning on DOM order. */}
            <Box
                sx={{
                    position: "relative",
                    isolation: "isolate",
                    // `vh` on a phone measures the viewport with the URL bar collapsed, so
                    // a 90vh hero overflows on load and settles only after a scroll. `svh`
                    // is the small-viewport unit — the height actually visible. Applied
                    // through @supports because a duplicate key in an sx object is not a
                    // CSS fallback, it is just the second value winning.
                    height: "min(90vh, 860px)",
                    "@supports (height: 1svh)": {
                        height: "min(90svh, 860px)",
                    },
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                {/* Background Video */}
                <video
                    src={videoSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0,
                    }}
                />

                {/* Scrim. A gradient rather than a flat 50% black: the copy sits in the
                    middle of the frame, so the darkening is concentrated top and bottom
                    where it buys contrast against the header and the fold, and eased off
                    through the centre so the footage stays visible. */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                            "linear-gradient(180deg, rgba(8,12,20,.78) 0%, rgba(8,12,20,.42) 38%, rgba(8,12,20,.52) 68%, rgba(8,12,20,.85) 100%)",
                        zIndex: 1
                    }}
                />

                {/* Hero Content */}
                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
                    <Stack spacing={3} alignItems="center" textAlign="center">
                        <Typography
                            variant="h1"
                            sx={{
                                color: "white",
                                // The scrim now carries the separation from the footage,
                                // so the old text-shadow is just blur on the letterforms.
                                maxWidth: '14ch',
                                px: { xs: 2, sm: 0 }
                            }}
                        >
                            Building Dreams Into Reality
                        </Typography>
                        <Typography
                            sx={{
                                color: alpha('#ffffff', 0.88),
                                maxWidth: '620px',
                                fontSize: { xs: '1.0625rem', md: '1.25rem' },
                                lineHeight: 1.6,
                                pt: 1,
                                pb: 2,
                                px: { xs: 2, sm: 0 }
                            }}
                        >
                            Expert curtain wall, cladding, and remediation services delivered with precision and excellence.
                        </Typography>
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={{ xs: 2, sm: 2 }}
                            sx={{ width: { xs: '100%', sm: 'auto' }, px: { xs: 3, sm: 0 } }}
                        >
                            <Button
                                component={Link}
                                to="/contact"
                                variant="contained"
                                size="large"
                                onClick={() => window.scrollTo(0, 0)}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    py: 1.75,
                                    px: 4,
                                    backgroundColor: theme.palette.primary.main,
                                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                                    fontSize: '1.0625rem',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Get Started
                            </Button>
                            <Button
                                component={Link}
                                to="/projects"
                                variant="outlined"
                                size="large"
                                onClick={() => window.scrollTo(0, 0)}
                                sx={{
                                    py: 1.75,
                                    px: 4,
                                    borderColor: alpha('#ffffff', 0.55),
                                    color: 'white',
                                    backdropFilter: 'blur(6px)',
                                    WebkitBackdropFilter: 'blur(6px)',
                                    backgroundColor: alpha('#ffffff', 0.06),
                                    '&:hover': {
                                        borderColor: theme.palette.common.white,
                                        backgroundColor: alpha('#ffffff', 0.16)
                                    },
                                    fontSize: '1.0625rem',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                View Our Work
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            {/* Features Section */}
            {/* Both card bands used to invert the surface pair against each other — this
                one put light cards on a dark band and the services band did the reverse.
                Now every band is the canvas and every card is the raised surface.
                No bottom padding: the services band that follows shares this background,
                so two full paddings in a row read as one large empty gap rather than as
                two sections. Its top padding is the whole separation. */}
            <Box sx={{ pt: SECTION_Y, pb: 0, backgroundColor: colors.background[200] }}>
                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Reveal index={index} sx={{ height: '100%' }}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            backgroundColor: colors.background[300],
                                            ...hoverLift(theme)
                                        }}
                                    >
                                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                            <Stack spacing={2} alignItems="center" textAlign="center">
                                                <Box sx={iconChip(theme, 56)}>{feature.icon}</Box>
                                                <Typography
                                                    variant="h5"
                                                    sx={{ color: theme.palette.text.primary }}
                                                >
                                                    {feature.title}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ color: theme.palette.text.secondary }}
                                                >
                                                    {feature.description}
                                                </Typography>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Services Section */}
            <Box sx={{ ...sectionPadding, backgroundColor: colors.background[200] }}>
                <Container maxWidth="lg">
                    <Reveal>
                        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 }, px: { xs: 2, sm: 0 } }}>
                            <Typography
                                variant="h2"
                                sx={{ color: theme.palette.text.primary }}
                            >
                                Our Services
                            </Typography>
                            {/* Short rule under the heading in place of a strapline —
                                keeps the section marked without inventing copy. */}
                            <Box
                                sx={{
                                    width: 56,
                                    height: 3,
                                    borderRadius: 2,
                                    mx: 'auto',
                                    mt: 2.5,
                                    backgroundColor: theme.palette.primary.main,
                                }}
                            />
                        </Box>
                    </Reveal>
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                        {services.map((service, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Reveal index={index} sx={{ height: '100%' }}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            backgroundColor: colors.background[300],
                                            ...hoverLift(theme)
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                height: { xs: 230, md: 210 },
                                                ...imageZoom
                                            }}
                                        >
                                            <img src={service.image} alt={service.title} />
                                        </Box>
                                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                            <Typography
                                                variant="h5"
                                                gutterBottom
                                                sx={{ color: theme.palette.text.primary }}
                                            >
                                                {service.title}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{ color: theme.palette.text.secondary }}
                                            >
                                                {service.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Call to Action */}
            <Box
                sx={{
                    ...sectionPadding,
                    px: { xs: 2, sm: 0 },
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.common.white,
                    // Off-centre glow so the band is not a flat rectangle of one blue.
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: `radial-gradient(120% 90% at 15% 0%, ${alpha('#ffffff', 0.16)} 0%, transparent 60%)`,
                        pointerEvents: 'none',
                    },
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative' }}>
                    <Stack spacing={3} alignItems="center" textAlign="center">
                        <Typography
                            variant="h3"
                            sx={{ color: 'inherit' }}
                        >
                            Ready to Start Your Project?
                        </Typography>
                        <Typography
                            sx={{
                                maxWidth: '600px',
                                color: alpha('#ffffff', 0.88),
                                fontSize: { xs: '1.0625rem', md: '1.1875rem' },
                                pb: 1,
                            }}
                        >
                            Contact us today for a free consultation and estimate.
                        </Typography>
                        <Button
                            component={Link}
                            to="/contact"
                            variant="contained"
                            size="large"
                            onClick={() => window.scrollTo(0, 0)}
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                py: 1.75,
                                px: 4,
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.dark,
                                '&:hover': {
                                    backgroundColor: alpha('#ffffff', 0.88)
                                },
                                fontSize: '1.0625rem',
                                width: { xs: '100%', sm: 'auto' }
                            }}
                        >
                            Contact Us
                        </Button>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default Home;
